// AvatarLoadOverlay — one continuous boot progress bar for the whole avatar
// load: the big DOWNLOADS (ORT wasm, riglogic.wasm, /ogs decoders, hair.gsh) then
// HAIR init (shader compile / lighting / finalize). Composes two stores into a
// single 0-100% bar with a live status line so the user always sees what's
// happening instead of a blank screen on a slow connection.
//
//   downloads → [0, 75%]   (assetLoadProgress, real bytes)   — hair avatars
//   hair init → [75, 100%] (hairLoadProgress, time-estimated steps)
//   downloads → [0, 100%]  when the avatar ships no hair (nothing to reserve for)
//
// Supersedes the old standalone HairLoadOverlay (hair is now the tail of this
// bar). Env-apply keeps its own SplatLoadingOverlay (different trigger), styled
// to match via the shared LoadProgressCard.
import React, { useEffect, useRef, useState } from 'react'
import { assetLoadProgress } from '../lib/assetLoadProgress.js'
import { hairLoadProgress } from '../lib/hairLoadProgress.js'
import LoadProgressCard, { fmtSize } from './LoadProgressCard.jsx'

const DOWNLOAD_SPAN = 0.75 // downloads fill the first 75% of the bar

export default function AvatarLoadOverlay() {
  const [dl, setDl] = useState(() => assetLoadProgress.get())
  const [hair, setHair] = useState(() => hairLoadProgress.get())
  useEffect(() => assetLoadProgress.subscribe(setDl), [])
  useEffect(() => hairLoadProgress.subscribe(setHair), [])
  // Download tasks register lazily (each as its fetch resolves headers), so the
  // aggregate denominator grows over the load and the raw fraction can drop —
  // e.g. a warm cached static asset lands at 75% before the cold /ogs files
  // register, snapping the bar backward. Hold a monotonic max so the bar only
  // ever advances; reset when the overlay clears.
  const maxFrac = useRef(0)

  // Reserve the [75,100%] hair tail only when hair is actually coming — either
  // already initialising (`active`) or known-present in the bundle (`expected`,
  // set by ogsContentPacks before downloads). A no-hair avatar reserves nothing,
  // so its download bar spans the full [0,100%] and reaches 100% instead of
  // stalling at 75% then vanishing.
  const reserveHairTail = hair.active || hair.expected
  if (!dl.active && !reserveHairTail) { maxFrac.current = 0; return null }

  let fraction, statusLine, subtitle, indeterminate = false
  if (hair.active) {
    // Hair init drives the tail.
    fraction = DOWNLOAD_SPAN + hair.fraction * (1 - DOWNLOAD_SPAN)
    statusLine = hair.label || 'Loading hair…'
    subtitle = 'Gaussian-splat hair — first load builds shaders'
  } else {
    // Download phase. Spans [0,75%] when a hair tail is reserved, else [0,100%].
    const span = reserveHairTail ? DOWNLOAD_SPAN : 1
    fraction = dl.indeterminate ? 0.03 : dl.fraction * span
    indeterminate = dl.indeterminate
    // Downloads done but hair hasn't started yet (reserved tail) → we're waiting
    // on the hair init to spin up; say so instead of a stale "Downloading…".
    statusLine = (!dl.active && reserveHairTail) ? 'Preparing hair…' : (dl.label || 'Downloading avatar…')
    subtitle = dl.totalBytes > 0 ? `${fmtSize(dl.receivedBytes)} / ${fmtSize(dl.totalBytes)}` : null
  }

  // Monotonic clamp — never let the displayed bar move backward (the indeterminate
  // stripe is exempt: it's a fixed sentinel, not real progress).
  if (!indeterminate) fraction = maxFrac.current = Math.max(maxFrac.current, fraction)

  return (
    <LoadProgressCard
      testId="avatar-load-overlay"
      title="Loading avatar…"
      fraction={fraction}
      indeterminate={indeterminate}
      statusLine={statusLine}
      subtitle={subtitle}
    />
  )
}
